import { type ReactNode, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Card, Heading, Icon, Text } from '@zillow/constellation-10';
import { IconChevronRightFilled } from '@zillow/constellation-icons';

import { useGetListingDetails, useGetListingRecommendations } from '../../hooks/queries/properties';

import HigherTierPackagesModal from './HigherTierPackagesModal';
import ListingPerformanceModal from './ListingPerformanceModal';
import ListingQualityModal from './ListingQualityModal';

import { useCompetitiveInsights } from './useCompetitiveInsights';

type ListingMetricCardProps = {
  title: string;
  value: string;
  valueContent?: ReactNode;
  body?: ReactNode;
  onClick: () => void;
};

const ListingMetricCard = ({ title, value, valueContent, body, onClick }: ListingMetricCardProps) => (
  <Card elevated={false} interactive={true} tone="neutral" css={{ padding: '600' }} onClick={onClick}>
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '200', height: '100%' }}>
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '200' }}>
        <Text textStyle="body-sm-bold">{title}</Text>
        <Icon render={<IconChevronRightFilled />} />
      </Box>
      <Box css={{ display: 'flex', alignItems: 'center', gap: '200', flexWrap: 'wrap' }}>
        {valueContent || <Heading level={4}>{value}</Heading>}
      </Box>
      {typeof body === 'string' ? (
        <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
          {body}
        </Text>
      ) : (
        body
      )}
    </Box>
  </Card>
);

const getQualityColor = (score: number) => {
  if (score >= 70) return 'text.express.insight.strong';
  if (score >= 50) return 'text.accent.yellow.hero';
  return 'text.express.empower.strong';
};

const InsightCards = () => {
  const router = useRouter();
  const { listingAlias } = router.query as { listingAlias: string };
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData } = useGetListingRecommendations({ listingAlias });

  const { topOverlapComps, userComp, rankings, isLoading } = useCompetitiveInsights(listingDetails);

  const [qualityModalOpen, setQualityModalOpen] = useState(false);
  const [performanceModalOpen, setPerformanceModalOpen] = useState(false);
  const [tierModalOpen, setTierModalOpen] = useState(false);

  const performance = listingDetails?.performance;
  const qualityScore = recommendationsData?.completeness?.score;

  const prospectsThisMonth = performance?.totalProspectCount ?? 0;
  const viewsThisMonth = performance?.viewCount ?? 0;

  const currentLeadRank = useMemo(() => {
    const userRank = rankings.find((r) => String(r.listingAlias) === String(listingAlias));
    return userRank?.leadsRank ?? null;
  }, [rankings, listingAlias]);

  const totalComps = topOverlapComps.length + (userComp ? 1 : 0);

  if (!listingDetails?.lastActivatedMs) return null;

  return (
    <>
      <Box
        css={{
          display: 'flex',
          gap: '300',
          marginTop: '400',
          marginBottom: '400',
          flexDirection: { base: 'column', md: 'row' },
        }}
      >
        <Box css={{ flex: 1 }}>
          <ListingMetricCard
            title="Listing quality"
            value={qualityScore === undefined ? '-' : `${qualityScore}%`}
            valueContent={
              qualityScore !== undefined ? (
                <Heading level={4} css={{ color: getQualityColor(qualityScore) }}>
                  {qualityScore}%
                </Heading>
              ) : undefined
            }
            onClick={() => setQualityModalOpen(true)}
            body={
              qualityScore !== undefined
                ? qualityScore < 70
                  ? 'Your listing is missing critical details'
                  : 'Your listing has all critical details'
                : undefined
            }
          />
        </Box>

        <Box css={{ flex: 1 }}>
          <ListingMetricCard
            title="Listing prospects (MTD)"
            value={String(prospectsThisMonth)}
            onClick={() => setPerformanceModalOpen(true)}
            body={`${viewsThisMonth} views MTD`}
          />
        </Box>

        <Box css={{ flex: 1 }}>
          <ListingMetricCard
            title="Lead rank"
            value={currentLeadRank !== null ? String(currentLeadRank) : '-'}
            valueContent={
              currentLeadRank !== null ? (
                <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
                  <Heading level={4}>{currentLeadRank}</Heading>
                  <Text textStyle="body-sm-bold">of</Text>
                  <Heading level={4}>{totalComps}</Heading>
                </Box>
              ) : undefined
            }
            onClick={() => setTierModalOpen(true)}
            body={`Compared with ${topOverlapComps.length} similar properties`}
          />
        </Box>
      </Box>

      {qualityModalOpen && (
        <ListingQualityModal
          onClose={() => setQualityModalOpen(false)}
          topComps={topOverlapComps}
          listingAlias={listingAlias}
        />
      )}
      {performanceModalOpen && (
        <ListingPerformanceModal
          onClose={() => setPerformanceModalOpen(false)}
          prospectsThisMonth={prospectsThisMonth}
          viewsThisMonth={viewsThisMonth}
          listingAlias={listingAlias}
        />
      )}
      {tierModalOpen && (
        <HigherTierPackagesModal
          onClose={() => setTierModalOpen(false)}
          topComps={topOverlapComps}
          rankings={rankings}
          listingAlias={listingAlias}
        />
      )}
    </>
  );
};

export default InsightCards;
